import * as Sentry from '@sentry/browser';
import { PLAYER_EVENTS, MEDIA_ELEMENT_EVENTS } from './enums';
import { PeerConnection, SessionDescription } from './adapters';
import WebRTCBase from './base';
import generateUUID from './utils';
import * as whipwhap from './whip-whap';

export default class Player extends WebRTCBase {
  /**
   * @param {HTMLMediaElement} media
   * @param {string} url
   * @param {object} opts
   * @param {boolean} opts.autoplay
   * @param {boolean} opts.videoDisabled - disable video transceiver
   * @param {boolean} opts.audioDisabled - disable audio transceiver
   * @param {number} opts.retryMax - number of connection retries
   * @param {number} opts.retryDelay - number of connection retries
   * @param {boolean} shouldLog
   * @param {func} opts.onMediaInfo - returns MediaInfo and ABR indicator
   * @param {string} opts.sentryConfig - Sentry dsn address
   * @param {boolean} opts.authTokenName
   * @param {boolean} opts.tokenValue
   * @param {string} opts.start_track - starting track for playback
   */

  constructor(media, url, opts = {}, shouldLog = false) {
    super(url, opts, shouldLog);

    if (!(media instanceof HTMLMediaElement)) {
      throw new Error('First argument must be html5 video tag');
    }

    this.remoteVideo = media;

    this.remoteVideo.addEventListener('play', this.onVideoPlay.bind(this));

    this.opts = opts;

    if ('start_track' in opts) {
      if (typeof opts.start_track !== 'string') {
        throw new Error('start_track must be string');
      } else {
        this.start_track = opts.start_track;
      }
    }

    if ('videoDisabled' in opts && opts.videoDisabled) {
      this.opts.videoDisabled = true;
    } else {
      this.opts.videoDisabled = false;
    }

    if ('audioDisabled' in opts && opts.audioDisabled) {
      if (this.opts.videoDisabled) {
        throw new Error('You must enable at least one transceiver');
      }
      this.opts.audioDisabled = true;
    } else {
      this.opts.audioDisabled = false;
    }

    if (opts.autoplay) {
      this.play();
    }

    if (opts.sentryConfig && opts.sentryConfig.length) {
      this.sentryConfig = opts.sentryConfig;
    }

    if (opts.onMediaInfo) {
      this.onMediaInfo = opts.onMediaInfo;
    }
    if (opts.onTrackInfo) {
      this.onTrackInfo = opts.onTrackInfo;
    }

    this.abr = false;

    this.onEventCallback = opts.onEvent;

    this.statsSendEnable = opts.statsSendEnable || true;
    this.statsSendTime = opts.statsSendTime * 1000 || 60000;
  }

  // Stats block start
  resetStats() {
    this.playerStatsObject = {
      proto: 'webrtc',
      user_agent: navigator.userAgent,
      bytes: 0,
      // player info
      application: {
        application_name: 'webrtc_player',
        application_version: WebRTCBase.version ? WebRTCBase.version : null,
        // counts
        stall_count: 0,
        pause_count: 0,
        error_count: 0,
        reconnect_count: 0,
        bitrate_change_count: 0,
        // durations
        live_duration: 0,
        stall_duration: 0,
        pause_duration: 0,
        // frames stats
        total_video_frames: 0,
        dropped_video_frames: 0,
        corrupted_video_frames: 0,
      },
    };
    if (this.statsSendEnable || this.opts.statsSendEnable) {
      this.startSendStats();
    }
  }

  onDestroy() {
    this.stop();
    this.remoteVideo.removeEventListener('play', this.onVideoPlay);
  }

  createPeerConnection(config = {}, url = '') {
    this.log('Player::createPeerConnection');
    this.remotePc = new PeerConnection(config);
    // {iceServers: [], bundlePolicy: "max-compat"}, [{"DtlsSrtpKeyAgreement": true}]
    this.remotePc.stream_id = 'remote1';

    if (!this.opts.videoDisabled) {
      this.remotePc.addTransceiver('video', { direction: 'sendrecv' }); // need SSRC in offer
    }
    if (!this.opts.audioDisabled) {
      this.remotePc.addTransceiver('audio', { direction: 'sendrecv' });
    }
    this.remotePc.addEventListener(
      'iceconnectionstatechange',
      whipwhap.handleIceStateChange,
    );
    this.remotePc.addEventListener('negotiationneeded', (ev) => {
      whipwhap
        .handleNegotiationNeeded(ev, url, {
          offerToReceiveAudio: !this.opts.audioDisabled,
          offerToReceiveVideo: !this.opts.videoDisabled,
        })
        .then((result) => {
          this.xsid = result;
        })
        .catch((err) => {
          this.log('handleNegotiationNeeded', err);
          this.addPlayerStat('error_count');
          this.addPlayerStat('reconnect_count');
          this.stopRetry(this.play);
        });
    });

    this.remotePc.ontrack = this.gotRemoteTrack.bind(this);
    this.remotePc.onconnectionstatechange = () => {
      if (!this.remotePc) {
        return;
      }
      this.log('Player::peer connection state:', this.remotePc.connectionState);
      if (
        this.remotePc.connectionState === 'closed' ||
        this.remotePc.connectionState === 'disconnected'
      ) {
        this.stop();
        this.notify(PLAYER_EVENTS.FAIL, 'peer_connection_closed');
      } else if (this.remotePc.connectionState === 'failed') {
        this.stopRetry(this.play);
      }
    };
    this.remotePc.ondatachannel = (err) => {
      console.debug('!!! ice candidate on data channel:', err);
    };

    // Logging
    this.remotePc.oniceconnectionstatechange = (evt) => {
      if (!this.remotePc) {
        return;
      }
      this.log('Player::ice connection change:', evt, {
        iceConnectionState: this.remotePc.iceConnectionState,
      });
      if (this.remotePc.iceConnectionState === 'failed') {
        this.stopRetry(this.play);
        this.notify(PLAYER_EVENTS.FAIL, 'ice_connection_fail');
      } else if (
        this.remotePc.iceConnectionState === 'closed' ||
        this.remotePc.iceConnectionState === 'disconnected' ||
        this.remotePc.iceConnectionState === undefined
      ) {
        this.notify(PLAYER_EVENTS.FAIL, 'ice_connection_closed');
        // разобраться почему в RTCPeerConnection не срабатывает restartIce
        // this.remotePc.restartIce();
        this.stopRetry(this.play);
      }
      this.notify(PLAYER_EVENTS.DEBUG, [
        'ICE connection state',
        this.remotePc && this.remotePc.iceConnectionState,
      ]);
    };

    this.remotePc.onicegatheringstatechange = (evt) => {
      this.log(
        'Player::ice gathering state',
        this.remotePc.iceGatheringState,
        evt,
      );
    };

    if (!this.progressListener) {
      this.startProgressListener();
    }
  }

  getUrl() {
    const url = new URL(this.url);
    url.pathname = `${url.pathname}/whep`;
    url.search =
      this.opts.authTokenName && this.opts.tokenValue
        ? `?${this.opts.authTokenName}=${this.opts.tokenValue}`
        : '';
    if (this.start_track) {
      url.search += `${
        this.opts.authTokenName && this.opts.tokenValue ? '&' : '?'
      }start_track=${this.start_track}`;
    }
    return url.toString();
  }

  play() {
    this.log('Player::play');
    if (this.remotePc) {
      this.stop();
    }
    // Stats
    this.resetStats();
    this.uuid = generateUUID();
    this.playerStatsObject.id = this.uuid;
    this.playerStatsObject.opened_at = Date.now();
    if (this.stopRetryTimeout) {
      clearTimeout(this.stopRetryTimeout);
    }

    this.addListeners();
    const url = this.getUrl();
    this.createPeerConnection(
      {
        bundlePolicy: 'max-bundle',
      },
      url,
    );
    this.getMediaInfo();

    if (process.env.NODE_ENV === 'production' && this.sentryConfig) {
      Sentry.init({
        dsn: this.sentryConfig,
        environment: process.env.NODE_ENV || 'development',
        release: `FlussonicWebrtcPlayer${
          WebRTCBase.version ? `@${WebRTCBase.version}` : ''
        }`,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
      });

      this.sentryEnabled = true;
    }
  }

  stop() {
    this.isFirstMsgReceived = false;
    this.retryCounterReset();

    if (this.remotePc) {
      this.remotePc.onconnectionstatechange = null;
      this.remotePc.ondatachannel = null;
      this.remotePc.onicecandidate = null;
      this.remotePc.onicecandidateerror = null;
      this.remotePc.oniceconnectionstatechange = null;
      this.remotePc.onicegatheringstatechange = null;
      this.remotePc.onremovestream = null;
      this.remotePc.ontrack = null;

      this.remotePc.close();
      delete this.remotePc;
    }

    /* Stats block start */
    // Track last stats before stop
    if (this.stallingStarted) {
      clearTimeout(this.probablyStalled);
      const { application } = this.playerStatsObject;
      if (application) {
        let { stall_duration } = application;
        stall_duration += Date.now() - this.stallingStarted;
        this.addPlayerStat('stall_duration', false, stall_duration);
        delete this.stallingStarted;
      }
    }

    delete this.frameWidth;
    this.playerStatsObject.closed_at = Date.now();
    // Send last stats and stop
    this.stopSendStats();
    /* Stats block end */

    this.removeListeners();
    // Stop tracking progress
    if (this.progressListener) {
      clearInterval(this.progressListener);
      delete this.progressListener;
    }

    try {
      this.remoteVideo.srcObject = null;
    } catch (err) {
      this.remoteVideo.src = null;
    }
  }

  gotRemoteTrack(e) {
    this.log('remote track', e);
    if (e.track.kind === 'video' || e.track.kind === 'audio') {
      try {
        if (this.remoteVideo.srcObject !== e.streams[0]) {
          const { streams } = e;
          const [stream] = streams;
          this.remoteVideo.srcObject = stream;
        }
      } catch (error) {
        if (this.sentryEnabled) {
          Sentry.captureException(error);
        }
        this.addPlayerStat('error_count');
        if (typeof window !== 'undefined') {
          this.remoteVideo.src = window.URL.createObjectURL(e.streams[0]);
        }
      }
    }
  }

  getSessionDescription({ sdp, ...message }) {
    const description = new SessionDescription({
      ...message,
      // I'm really shamed, but I experimented and hadn't find any other reason
      // for not playing video, so SODD helps me =)
      // https://stackoverflow.com/questions/47990094/failed-to-set-remote-video-description-send-parameters-on-native-ios
      sdp: sdp.replace(
        /profile-level-id=(\d|A|B|C|D|E|F)+;/,
        'profile-level-id=42E01F;',
      ),
    });
    return description;
  }

  remoteCreateAnswer() {
    this.log('Player::success adding offer');
    // Теперь просим PeerConnection сделать ответ для флюссоника
    return this.remotePc.createAnswer();
  }

  async remoteSetLocalDescription(answer) {
    this.log('Player::now have answer', answer.sdp);
    // Получили ответ от PeerConnection и помечаем его как local description
    const resultAnswer = await this.remotePc.setLocalDescription(answer);
    return resultAnswer;
  }

  onDescriptionError(reason) {
    if (this.sentryEnabled) {
      Sentry.captureException(reason);
    }
    this.notify(PLAYER_EVENTS.DEBUG, ['failed to set remote answer', reason]);
    // stop() caused problems with playback
    // this.stop();
    this.notify(PLAYER_EVENTS.FAIL, 'set_remote_description_failed');
  }

  onVideoPlay() {
    this.notify(PLAYER_EVENTS.PLAY);
  }

  onGetMediaInfo() {
    if (this.onMediaInfo) {
      this.onMediaInfo(this.mediaInfoTracks, this.abr);
    }
    if (this.mediaInfoTracks.length) {
      let haveVideo = false;
      let haveAudio = false;
      this.mediaInfoTracks.forEach((track) => {
        if (haveVideo && haveAudio) return;
        const { content } = track;
        if (content === 'video') {
          haveVideo = true;
        }
        if (content === 'audio') {
          haveAudio = true;
        }
      });
      if (!haveVideo) {
        this.opts.videoDisabled = true;
      }
      if (!haveAudio) {
        if (this.opts.videoDisabled) {
          this.stop();
          throw new Error('You have got no video and no audio transceiver');
        }
        this.opts.audioDisabled = true;
      }
    }
    return this.mediaInfoTracks;
  }

  onPlayingInfo(info) {
    if (this.onTrackInfo) {
      this.addPlayerStat('bitrate_change_count');
      this.onTrackInfo(info);
    }
  }

  changeTracks(tracks) {
    if (!Array.isArray(tracks)) {
      console.error('tracks must be an array');
      return;
    }
    if (tracks[0] === 'auto') {
      this.trackSwitch([]);
    } else {
      this.trackSwitch(tracks);
    }
    this.addPlayerStat('bitrate_change_count');
  }

  getMediaInfo() {
    fetch(`${this.url}/media_info.json${this.getAuthToken()}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((response) => response.json())
      .then((result) => {
        this.mediaInfoTracks = result.tracks;
        this.onGetMediaInfo();
      })
      .catch((error) => {
        this.log('get MediaInfo error:', error);
        this.addPlayerStat('error_count');
        this.addPlayerStat('reconnect_count');
        this.stopRetry(this.play);
      });
  }

  trackSwitch(tracks) {
    fetch(
      `${this.url}/sessions/${
        this.xsid ? this.xsid : ''
      }/request_tracks${this.getAuthToken()}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify({ tracks }),
      },
    )
      .then((data) => {
        this.log('trackSwitch', data);
      })
      .catch((error) => {
        this.log('track switch error:', error);
      });
  }

  onEvent(event) {
    const { type } = event;
    const { application } = this.playerStatsObject;
    switch (type) {
      case 'playing':
        if (!this.playerStatsObject.started_at) {
          this.playerStatsObject.started_at = Date.now();
          this.playerStatsObject.updated_at = Date.now();
        }
        this.playbackSegmentStart = Date.now();
        if (this.pauseStarted) {
          if (application) {
            let { pause_duration } = application;
            pause_duration += Date.now() - this.pauseStarted;
            this.addPlayerStat('pause_duration', false, pause_duration);
            delete this.pauseStarted;
          }
        }
        if (!this.progressListener) {
          this.startProgressListener();
        }
        break;
      case 'pause':
        if (this.playerStatsObject.started_at) {
          this.addPlayerStat('pause_count');
          this.pauseStarted = Date.now();
        }
        if (this.progressListener) {
          clearInterval(this.progressListener);
          delete this.progressListener;
        }
        break;
      case 'stalled':
        if (this.playerStatsObject.started_at) {
          this.addPlayerStat('stall_count');
          this.stallingStarted = Date.now();
        }
        if (this.progressListener) {
          clearInterval(this.progressListener);
          delete this.progressListener;
        }
        break;
      default:
        break;
    }

    if (this.onEventCallback) {
      this.onEventCallback(event);
    }
  }

  startProgressListener() {
    this.progressListener = setInterval(() => {
      if (this.remotePc) {
        this.remotePc.getStats(null).then((stats) => {
          stats.forEach((report) => {
            if (report.type === 'transport') {
              const {
                bytesReceived,
                timestamp,
                // bytesSent,
                // packetsReceived,
                // packetsSent,
              } = report;
              // console.debug({
              //   report,
              //   bytesReceived,
              //   timestamp,
              // bytesSent,
              // packetsReceived,
              // packetsSent,
              // });
              this.playerStatsObject.bytes = bytesReceived;
              this.playerStatsObject.updated_at = Math.round(timestamp);

              if (this.playbackSegmentStart) {
                const { application } = this.playerStatsObject;
                if (application) {
                  let { live_duration } = application;
                  live_duration += Date.now() - this.playbackSegmentStart;
                  this.addPlayerStat('live_duration', false, live_duration);
                  delete this.playbackSegmentStart;
                }
              }
              this.playbackSegmentStart = timestamp;
              // } else if (report.type === 'inbound-rtp') {
              //   if ('kind' in report) {
              //     if (report.kind === 'video') {
              //       console.log('inbound-rtp', report);
              //     }
              //   }
            } else if (report.type === 'track') {
              if ('kind' in report) {
                if (report.kind === 'video') {
                  if (report.frameWidth) {
                    if (report.frameWidth > 0) {
                      if (this.frameWidth) {
                        if (this.frameWidth !== report.frameWidth) {
                          this.addPlayerStat('bitrate_change_count');
                          this.frameWidth = report.frameWidth;
                        }
                      } else {
                        this.frameWidth = report.frameWidth;
                      }
                    }
                  }

                  if (
                    report.framesReceived ===
                    this.playerStatsObject.application.total_video_frames
                  ) {
                    const peerStalled = () => {
                      if (!this.stallingStarted) {
                        this.addPlayerStat('stall_count');
                        this.stallingStarted = Date.now();
                      }
                    };
                    this.probablyStalled = setTimeout(peerStalled, 500);
                  } else {
                    clearTimeout(this.probablyStalled);
                    const { application } = this.playerStatsObject;
                    if (this.stallingStarted) {
                      if (application) {
                        let { stall_duration } = application;
                        stall_duration += Date.now() - this.stallingStarted;
                        this.addPlayerStat(
                          'stall_duration',
                          false,
                          stall_duration,
                        );
                        delete this.stallingStarted;
                      }
                    }
                  }
                  const vQuality = this.remoteVideo.getVideoPlaybackQuality();
                  const { corruptedVideoFrames } = vQuality;
                  this.addPlayerStat(
                    'total_video_frames',
                    false,
                    report.framesReceived,
                  );
                  this.addPlayerStat(
                    'dropped_video_frames',
                    false,
                    report.framesDropped,
                  );
                  this.addPlayerStat(
                    'corrupted_video_frames',
                    false,
                    corruptedVideoFrames,
                  );
                }
              }
            }
          });
        });
      }
    }, 1000);
  }

  addListeners() {
    this.remoteVideo.addEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_STALLED,
      this.onEvent.bind(this),
      true,
    );
    this.remoteVideo.addEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_PLAYING,
      this.onEvent.bind(this),
      true,
    );
    this.remoteVideo.addEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_WAITING,
      this.onEvent.bind(this),
      true,
    );
    this.remoteVideo.addEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_PAUSE,
      this.onEvent.bind(this),
      true,
    );
  }

  removeListeners() {
    this.remoteVideo.removeEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_STALLED,
      this.onEvent,
      true,
    );
    this.remoteVideo.removeEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_PLAYING,
      this.onEvent,
      true,
    );
    this.remoteVideo.removeEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_WAITING,
      this.onEvent,
      true,
    );
    this.remoteVideo.removeEventListener(
      MEDIA_ELEMENT_EVENTS.MEDIA_ELEMENT_PAUSE,
      this.onEvent,
      true,
    );
  }

  addPlayerStat(name, count = true, data = null) {
    const { application } = this.playerStatsObject;
    if (application) {
      if (data) {
        application[name] = Math.round(data);
      } else if (count) {
        application[name] += 1;
      }
      this.playerStatsObject.application = application;
    }
  }

  getStats() {
    if (this.xsid) {
      this.playerStatsObject.source_id = this.xsid;
    }
    return {
      ...this.playerStatsObject,
      id: this.uuid,
    };
  }

  startSendStats() {
    this.sendingInterval = setInterval(() => {
      this.postStats();
    }, this.statsSendTime || 60000);
  }

  stopSendStats() {
    if (!this.sendingInterval) {
      return;
    }
    clearInterval(this.sendingInterval);
    this.postStats();
  }

  getStatsUrl() {
    return `${this.url}/sessions/${
      this.xsid ? this.xsid : ''
    }${this.getAuthToken()}`;
  }

  getAuthToken() {
    return this.opts.authTokenName && this.opts.tokenValue
      ? `?${this.opts.authTokenName}=${this.opts.tokenValue}`
      : '';
  }

  postStats() {
    if (!this.playerStatsObject.started_at) {
      return;
    }

    fetch(this.getStatsUrl(), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(this.getStats()),
    })
      .then(() => {})
      .catch((error) => {
        this.log('Stat send error:', error);
      });
  }
}
